import { ReactNode, useEffect, useState } from 'react';
import { FormControlLabel, Switch, Tooltip, Typography } from '@material-ui/core';
import {
  Content,
  ContentHeader,
  Header,
  Link,
  Page,
  Progress,
  StatusAborted,
  StatusError,
  StatusOK,
  StatusPending,
  StatusRunning,
  Table,
  TableColumn,
  WarningPanel,
} from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';

type Pr = { system: 'github' | 'azure'; repo: string; title: string; url: string; state: 'open' | 'merged' | 'closed' };
type Task = {
  id: string;
  status: string;
  createdAt: string;
  createdBy?: string;
  spec: { templateInfo?: { entityRef?: string }; parameters: Record<string, any> };
};
type Row = { id: string; date: Date; request: string; user: string; branch: string; status: string; prs?: Pr[]; error?: string };

const DAYS = 30;
const DEVOPS = 'group:default/devops';
const CONCURRENCY = 4;

// Misma rama que abre cada plantilla (templates/create-repository/*-template.yaml del catálogo).
const branchOf = (task: Task) => {
  if (task.spec.templateInfo?.entityRef?.endsWith('request-argo-values')) return `self-service-${task.id}`;
  if (task.spec.templateInfo?.entityRef?.endsWith('request-external-route')) return `route-request-${task.id}`;
  if (task.spec.templateInfo?.entityRef?.endsWith('request-user-onboarding')) {
    return task.spec.parameters.username ? `user-request-${task.spec.parameters.username}` : undefined;
  }
  const { squad, key, stack, kind, cloud, repo } = task.spec.parameters;
  if (task.spec.templateInfo?.entityRef?.endsWith('request-repository-archive')) return repo ? `repo-archive-${repo}` : undefined;
  if (task.spec.templateInfo?.entityRef?.endsWith('request-repository-iac')) {
    return kind && cloud && key ? `repo-request-iac-${kind}-${cloud}-${key}` : undefined;
  }
  if (task.spec.templateInfo?.entityRef?.endsWith('request-repository-ai')) {
    return kind && key ? `repo-request-${kind}-ai-${key}` : undefined;
  }
  const type = task.spec.templateInfo?.entityRef?.match(/request-repository-(msa|lib|spa)$/)?.[1];
  if (!type || !squad || !key) return undefined;
  const base = `repo-request-${type}-${squad === 'arch' ? 'arq' : squad}-${key}`;
  return type === 'lib' ? `${base}-${stack}` : base;
};

// Qué crea cada PR, por el repo al que apunta: [etiqueta corta, descripción].
const PURPOSE: [RegExp, string, string][] = [
  [/github-administration$/, 'Repo', 'Crear el repositorio'],
  [/pipeline-files$/, 'Pipelines', 'Pipelines del repo'],
  [/backstage-catalog$/, 'Catálogo', 'Ficha en el catálogo'],
  [/deuna-iac-do-ecr$/, 'ECR', 'Registro de imágenes (ECR)'],
  [/deuna-iac-do-argo-cd$|deuna-iac-do-eks-main-addons$/, 'Argo IaC', 'Aplicación en Argo CD'],
  [/deuna-iac-do-eks-external-traffic$/, 'Ruta', 'Ruta pública (Istio)'],
  [/deuna-iac-do-k8s-/, 'K8s', 'Recursos en Kubernetes'],
  [/argo-cd(-internal-tools)?$/, 'Despliegue', 'Configuración de despliegue (Argo)'],
];
const purposeOf = (repo: string, title = '') => {
  // En un alta de persona, el PR de github-administration es el equipo, no un repo nuevo.
  if (/github-administration$/.test(repo) && /^feat\(teams\)/.test(title)) return { short: 'GitHub', long: 'Equipo de GitHub' };
  if (/github-administration$/.test(repo) && /^chore\(repos\): archive/.test(title)) return { short: 'Repo', long: 'Archivar el repositorio' };
  const found = PURPOSE.find(([re]) => re.test(repo));
  const name = repo.split('/').pop() ?? repo;
  return found ? { short: found[1], long: found[2] } : { short: name, long: name };
};

const requestName = (task: Task, branch: string) => {
  if (branch.startsWith('self-service-')) {
    const { component = '', environment = '' } = task.spec.parameters;
    return `Cambio en ${String(component).split('/').pop()} (${environment})`;
  }
  // iac: repo-request-iac-<tipo>-<nube>-<clave> → deuna-iac-cl-<nube>-<clave>-spoke | deuna-iac-cl-shiftleft-<nube>-<clave>.
  const iac = branch.match(/^repo-request-iac-(spoke|module)-([a-z0-9]+)-(.+)$/);
  if (iac) return iac[1] === 'spoke' ? `deuna-iac-cl-${iac[2]}-${iac[3]}-spoke` : `deuna-iac-cl-shiftleft-${iac[2]}-${iac[3]}`;
  if (branch.startsWith('repo-archive-')) return `Archivar ${branch.slice(13)}`;
  if (branch.startsWith('route-request-')) {
    const { uriPrefix = '', component = '' } = task.spec.parameters;
    return `Ruta ${uriPrefix} → ${String(component).split('/').pop()}`;
  }
  return branch.startsWith('user-request-') ? `Alta de ${branch.slice(13)}` : branch.replace(/^repo-request-/, 'deuna-');
};

const PR_STATE: Record<Pr['state'], { text: string; icon: (c: ReactNode) => ReactNode }> = {
  merged: { text: 'aprobado y mergeado', icon: c => <StatusOK>{c}</StatusOK> },
  open: { text: 'esperando revisión', icon: c => <StatusPending>{c}</StatusPending> },
  closed: { text: 'cerrado sin merge', icon: c => <StatusError>{c}</StatusError> },
};

// Lo que ve el solicitante: lista cuando todo está mergeado; si algo se cerró sin merge, no se completará.
const summary = (row: Row): ReactNode => {
  if (row.error) return <StatusAborted>No se pudo consultar ({row.error})</StatusAborted>;
  if (row.status === 'failed') return <StatusError>La plantilla falló</StatusError>;
  if (row.status !== 'completed') return <StatusRunning>Creándose</StatusRunning>;
  if (!row.prs) return <StatusRunning>Consultando…</StatusRunning>;
  if (!row.prs.length) return <StatusAborted>Sin PRs encontrados</StatusAborted>;
  const closed = row.prs.filter(p => p.state === 'closed');
  const merged = row.prs.filter(p => p.state === 'merged').length;
  if (closed.length === row.prs.length) return <StatusError>Rechazada: se cerraron todos sus PRs</StatusError>;
  if (closed.length) {
    return <StatusError>Rechazada: se cerró {closed.map(p => purposeOf(p.repo, p.title).long).join(', ')}</StatusError>;
  }
  if (merged === row.prs.length) return <StatusOK>Lista</StatusOK>;
  return <StatusPending>En revisión: {merged} de {row.prs.length} aprobados</StatusPending>;
};

const prList = (row: Row) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: 16, rowGap: 4 }}>
    {(row.prs ?? []).map(pr => {
      const { short, long } = purposeOf(pr.repo, pr.title);
      return (
        <Tooltip key={pr.url} title={`${long}: ${PR_STATE[pr.state].text} (${pr.repo})`}>
          <span style={{ whiteSpace: 'nowrap' }}>
            {PR_STATE[pr.state].icon(<Link to={pr.url}>{short}</Link>)}
          </span>
        </Tooltip>
      );
    })}
  </div>
);

const columns = (all: boolean): TableColumn<Row>[] => [
  {
    title: 'Fecha',
    field: 'date',
    type: 'datetime',
    width: '10%',
    render: row => (
      <>
        {row.date.toLocaleDateString('es-EC')}
        <Typography variant="caption" color="textSecondary" component="div">
          {row.date.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })}
        </Typography>
      </>
    ),
  },
  { title: 'Solicitud', field: 'request', width: '22%' },
  ...(all ? [{ title: 'Pedido por', field: 'user', width: '12%' } as TableColumn<Row>] : []),
  { title: 'Estado', width: '26%', render: summary },
  { title: 'Pull requests (pasa el ratón para ver el detalle)', render: prList, sorting: false },
];

/** Solicitudes de repos hechas en Backstage y el estado de cada PR que abrieron; devops puede ver las de todos. */
export const MyRequestsPage = () => {
  const discovery = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const identity = useApi(identityApiRef);
  const [isDevops, setIsDevops] = useState(false);
  const [all, setAll] = useState(false);
  const [rows, setRows] = useState<Row[]>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    identity.getBackstageIdentity().then(i => setIsDevops(i.ownershipEntityRefs.includes(DEVOPS)));
  }, [identity]);

  useEffect(() => {
    let cancelled = false;
    setRows(undefined);
    (async () => {
      const { userEntityRef } = await identity.getBackstageIdentity();
      const scaffolder = await discovery.getBaseUrl('scaffolder');
      const who = all ? '' : `createdBy=${encodeURIComponent(userEntityRef)}&`;
      const res = await fetch(`${scaffolder}/v2/tasks?${who}limit=200&order=desc:created_at`);
      if (!res.ok) throw new Error(`El scaffolder respondió ${res.status}`);
      const since = Date.now() - DAYS * 24 * 3600 * 1000;
      const tasks = ((await res.json()) as { tasks: Task[] }).tasks.filter(t => Date.parse(t.createdAt) >= since);
      const base: Row[] = tasks.flatMap(task => {
        const branch = branchOf(task);
        return branch
          ? [{
              id: task.id,
              date: new Date(task.createdAt),
              request: requestName(task, branch),
              user: (task.createdBy ?? '').replace(/^user:(default\/)?/, ''),
              branch,
              status: task.status,
            }]
          : [];
      });
      if (cancelled) return;
      setRows(base);
      // Pocas a la vez y pintando según llegan: la búsqueda de GitHub admite 30 consultas por minuto.
      const statusUrl = await discovery.getBaseUrl('request-status');
      const filled = [...base];
      for (let i = 0; i < base.length && !cancelled; i += CONCURRENCY) {
        await Promise.all(
          base.slice(i, i + CONCURRENCY).map(async (row, j) => {
            const r = await fetch(`${statusUrl}/prs?branch=${encodeURIComponent(row.branch)}`);
            filled[i + j] = r.ok ? { ...row, prs: (await r.json()) as Pr[] } : { ...row, error: `HTTP ${r.status}` };
          }),
        );
        if (!cancelled) setRows([...filled]);
      }
    })().catch(e => setError(String(e?.message ?? e)));
    return () => {
      cancelled = true;
    };
  }, [all, discovery, fetch, identity]);

  const title = all ? 'Todas las solicitudes' : 'Mis solicitudes';
  return (
    <Page themeId="tool">
      <Header title={title} subtitle={`Repos pedidos en Backstage en los últimos ${DAYS} días y en qué va cada pull request`} />
      <Content>
        {isDevops && (
          <ContentHeader title="">
            <FormControlLabel
              control={<Switch checked={all} onChange={e => setAll(e.target.checked)} color="primary" />}
              label="Ver las de todos (devops)"
            />
          </ContentHeader>
        )}
        {error && <WarningPanel title="No se pudieron cargar las solicitudes" message={error} />}
        {!rows && !error && <Progress />}
        {rows && (
          <Table<Row>
            options={{ paging: true, pageSize: 20, search: true, padding: 'dense' }}
            columns={columns(all)}
            data={rows}
            emptyContent={<div style={{ padding: 16 }}>No hay solicitudes de repos en los últimos {DAYS} días.</div>}
          />
        )}
      </Content>
    </Page>
  );
};
